import { getPublicSiteConfigUrl } from "@Front/Api/helpers/publicApiUrls";
import { FrontendVariables } from "@Front/Config/VariablesFront";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";
import {
	DEFAULT_PUBLIC_LEGAL_FOOTER_DOCUMENTS_PREFIX,
	type PublicLegalFooterDocumentPdfPaths,
	PUBLIC_LEGAL_FOOTER_DOCUMENT_PDF_PATHS,
	PUBLIC_USER_GUIDE_ENTRY_PATH,
} from "le-coffre-resources/dist/publicLegalStaticPaths";
import { normalizePublicSiteDisplayName } from "./publicSiteDisplayName";
import { assertLoadedPublicSiteConfigBrandingMatchesLine } from "./publicSiteConfigLoaderBrandingAssert";
import type {
	LoadedPublicSiteConfig,
	PublicLegalStaticLoaded,
	PublicSiteIntegrationFlags,
	PublicSitePublicUiOptions,
} from "./publicSiteConfigLoaderTypes";
import { DEFAULT_PUBLIC_SITE_PUBLIC_UI_OPTIONS } from "./publicSiteConfigLoaderTypes";
import { publicUiOptionsPeekFallbackBeforeConfigLoad } from "./publicUiPeekFallback";

export type {
	LoadedPublicSiteConfig,
	PublicLegalStaticLoaded,
	PublicSiteIntegrationFlags,
	PublicSitePublicUiOptions,
} from "./publicSiteConfigLoaderTypes";

/**
 * Site label from a config object produced by {@link loadPublicSiteConfig} (or the in-memory cache).
 * Does not trim or re-parse: `display_name` is already normalized on the fetch path.
 */
export function siteDisplayNameFromLoadedConfig(cfg: LoadedPublicSiteConfig): string | null {
	const v = cfg.display_name;
	return typeof v === "string" && v.length > 0 ? v : null;
}

function emptyFlags(): PublicSiteIntegrationFlags {
	return {
		uses_idnot: false,
		uses_docv_oauth: false,
		uses_mailchimp: false,
		uses_ovh_sms: false,
		uses_stripe: false,
		allows_v1_import: false,
	};
}

function normalizeFlags(raw: unknown): PublicSiteIntegrationFlags {
	const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
	return {
		uses_idnot: Boolean(o["uses_idnot"]),
		uses_docv_oauth: Boolean(o["uses_docv_oauth"]),
		uses_mailchimp: Boolean(o["uses_mailchimp"]),
		uses_ovh_sms: Boolean(o["uses_ovh_sms"]),
		uses_stripe: Boolean(o["uses_stripe"]),
		allows_v1_import: Boolean(o["allows_v1_import"]),
	};
}

function readBool(o: Record<string, unknown>, key: string, fallback: boolean): boolean {
	const v = o[key];
	return typeof v === "boolean" ? v : fallback;
}

function readHeaderLogoVariant(o: Record<string, unknown>, fallback: PublicSitePublicUiOptions["header_logo_variant"]): PublicSitePublicUiOptions["header_logo_variant"] {
	const v = o["header_logo_variant"];
	if (v === "compact" || v === "default") {
		return v;
	}
	return fallback;
}

function normalizePublicUi(raw: unknown): PublicSitePublicUiOptions {
	const b = DEFAULT_PUBLIC_SITE_PUBLIC_UI_OPTIONS;
	if (!raw || typeof raw !== "object") {
		return b;
	}
	const o = raw as Record<string, unknown>;
	return {
		uses_operation_folder_lexicon: readBool(o, "uses_operation_folder_lexicon", b.uses_operation_folder_lexicon),
		login_show_center_brand_mark: readBool(o, "login_show_center_brand_mark", b.login_show_center_brand_mark),
		login_callback_show_lecoffre_logo: readBool(o, "login_callback_show_lecoffre_logo", b.login_callback_show_lecoffre_logo),
		header_logo_variant: readHeaderLogoVariant(o, b.header_logo_variant),
		merge_dark_public_shell_theme: readBool(o, "merge_dark_public_shell_theme", b.merge_dark_public_shell_theme),
		login_show_idnot_notary_connection: readBool(o, "login_show_idnot_notary_connection", b.login_show_idnot_notary_connection),
		uses_estate_workspace_terms_in_ui_copy: readBool(o, "uses_estate_workspace_terms_in_ui_copy", b.uses_estate_workspace_terms_in_ui_copy),
		my_account_show_idnot_portal_link: readBool(o, "my_account_show_idnot_portal_link", b.my_account_show_idnot_portal_link),
	};
}

function normalizeOptionalUrl(raw: unknown): string | null {
	return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
}

function defaultPublicLegalStaticLoaded(): PublicLegalStaticLoaded {
	return {
		footer_documents_prefix: DEFAULT_PUBLIC_LEGAL_FOOTER_DOCUMENTS_PREFIX,
		user_guide_entry_path: PUBLIC_USER_GUIDE_ENTRY_PATH,
		document_pdf_paths: PUBLIC_LEGAL_FOOTER_DOCUMENT_PDF_PATHS,
	};
}

function isPublicLegalFooterDocumentPdfPaths(v: unknown): v is PublicLegalFooterDocumentPdfPaths {
	if (!v || typeof v !== "object") {
		return false;
	}
	const o = v as Record<string, unknown>;
	const keys: (keyof PublicLegalFooterDocumentPdfPaths)[] = [
		"legalMentions",
		"confidentiality",
		"cookies",
		"cgs",
		"cgu",
	];
	return keys.every((k) => typeof o[k] === "string" && String(o[k]).startsWith("/"));
}

function normalizePublicLegalStaticLoaded(raw: unknown): PublicLegalStaticLoaded {
	const base = defaultPublicLegalStaticLoaded();
	const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
	if (!o) {
		return base;
	}
	const prefixRaw = o["footer_documents_prefix"];
	const guideRaw = o["user_guide_entry_path"];
	const pathsRaw = o["document_pdf_paths"];
	const footer_documents_prefix =
		typeof prefixRaw === "string" && prefixRaw.trim().startsWith("/")
			? prefixRaw.trim()
			: base.footer_documents_prefix;
	const user_guide_entry_path =
		typeof guideRaw === "string" && guideRaw.trim().startsWith("/")
			? guideRaw.trim()
			: base.user_guide_entry_path;
	const document_pdf_paths = isPublicLegalFooterDocumentPdfPaths(pathsRaw) ? pathsRaw : base.document_pdf_paths;
	return { footer_documents_prefix, user_guide_entry_path, document_pdf_paths };
}

function normalizeBranding(raw: unknown): LoadedPublicSiteConfig["branding"] | undefined {
	const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
	if (!o) {
		return undefined;
	}
	const logo_main_url = normalizeOptionalUrl(o["logo_main_url"]);
	const logo_small_url = normalizeOptionalUrl(o["logo_small_url"]);
	const favicon_url = normalizeOptionalUrl(o["favicon_url"]);
	if (!logo_main_url && !logo_small_url && !favicon_url) {
		return undefined;
	}
	return { logo_main_url, logo_small_url, favicon_url };
}

let memory: LoadedPublicSiteConfig | null = null;
let inflight: Promise<LoadedPublicSiteConfig> | null = null;

type PublicSiteConfigApiBody = {
	site_code?: unknown;
	public_ui?: unknown;
	integration_flags?: unknown;
	display_name?: unknown;
	product_code?: string;
	theme?: { css_vars?: Record<string, string> };
	branding?: unknown;
	public_legal_static?: unknown;
};

function loadedPublicSiteConfigWhenResponseNotOk(): LoadedPublicSiteConfig {
	return {
		public_ui: publicUiOptionsPeekFallbackBeforeConfigLoad(),
		integration_flags: emptyFlags(),
		public_legal_static: defaultPublicLegalStaticLoaded(),
	};
}

function loadedPublicSiteConfigFromApiBody(body: PublicSiteConfigApiBody): LoadedPublicSiteConfig {
	const siteRaw = body.site_code;
	const site_code = typeof siteRaw === "string" && siteRaw.trim().length > 0 ? siteRaw.trim() : undefined;
	const display_name = normalizePublicSiteDisplayName(body.display_name);
	return {
		site_code,
		public_ui: normalizePublicUi(body.public_ui),
		integration_flags: normalizeFlags(body.integration_flags),
		display_name,
		product_code: typeof body.product_code === "string" ? body.product_code : undefined,
		theme: body.theme,
		branding: normalizeBranding(body.branding),
		public_legal_static: normalizePublicLegalStaticLoaded(body.public_legal_static),
	};
}

async function fetchPublicSiteConfigFromUrl(url: string): Promise<LoadedPublicSiteConfig> {
	try {
		const res = await fetch(url, { method: "GET", credentials: "omit" });
		if (!res.ok) {
			return loadedPublicSiteConfigWhenResponseNotOk();
		}
		const body = (await res.json()) as PublicSiteConfigApiBody;
		return loadedPublicSiteConfigFromApiBody(body);
	} catch (e: unknown) {
		LoggerService.warn("[PublicSiteConfig] fetch failed", { error: coalesceThrownApiErrorText(e) });
		return loadedPublicSiteConfigWhenResponseNotOk();
	}
}

/**
 * Fetches `GET .../public/site-config` once per page load (shared cache).
 * Login notaire (IdNot) suit `integration_flags` depuis la table `sites`, pas `NEXT_PUBLIC_SITE_CODE`.
 */
export async function loadPublicSiteConfig(): Promise<LoadedPublicSiteConfig> {
	if (memory) {
		return memory;
	}
	if (inflight) {
		return inflight;
	}
	const vars = FrontendVariables.getInstance();
	if (!vars.BACK_API_HOST) {
		memory = {
			public_ui: publicUiOptionsPeekFallbackBeforeConfigLoad(),
			integration_flags: emptyFlags(),
			public_legal_static: defaultPublicLegalStaticLoaded(),
		};
		return memory;
	}
	const url = getPublicSiteConfigUrl();
	inflight = fetchPublicSiteConfigFromUrl(url)
		.then((cfg) => {
			const safe = assertLoadedPublicSiteConfigBrandingMatchesLine(cfg);
			memory = safe;
			return safe;
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
}

/** Lecture synchrone du cache `loadPublicSiteConfig` ; avant cache, repli aligné sur la ligne de build. */
export function peekPublicUiOptions(): PublicSitePublicUiOptions {
	return memory?.public_ui ?? publicUiOptionsPeekFallbackBeforeConfigLoad();
}
